import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { createBranchUser, fetchBranchUsers, updateBranchUser } from "../../../slices/company/branchUserSlice";
import { fetchBranches } from "../../../slices/company/branchSlice";
import { fetchRoles } from "../../../slices/company/rolesSlice";
import Select from "react-select";
import Swal from "sweetalert2";
import axios from "axios";
import { API_URL } from "../../../utils/constant";


const AddEditBranchUser = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [warehouses, setWarehouses] = useState([]);
    const branchUserData = location.state?.branchUserData;
    const { branches } = useSelector((state) => state.branches);
    const { roles } = useSelector((state) => state.companyRoles) || {};
    const { branchUsers } = useSelector((state) => state.branchUsers);

    const [formValues, setFormValues] = useState({
        name: branchUserData?.name || '',
        email: branchUserData?.email || '',
        mobile_no: branchUserData?.mobile_no || '',
        password: '', // password should be empty for editing
        confirmPassword: '', // for password confirmation
        roleId: branchUserData?.roleId || '',
        branch_id: branchUserData?.branch_id || '',
        parent_user: branchUserData?.parent_user ? branchUserData.parent_user.split(',').map(id => id.trim()) : [],
        account_holder_name: branchUserData?.account_holder_name || '',
        account_number: branchUserData?.account_number || '',
        bank_name: branchUserData?.bank_name || '',
        bank_ifsc: branchUserData?.bank_ifsc || '',
        bank_location: branchUserData?.bank_location || '',
        tax_payer_id: branchUserData?.tax_payer_id || '',
        warehouse_id: branchUserData?.warehouse_id || ''
    });

    const [error, setError] = useState("");


    const handleMultiSelectChange = (selectedOptions) => {
        setFormValues((prev) => ({
            ...prev,
            parent_user: selectedOptions.map(option => option.value) || []
        }));
    };

    const fetchWarehouses = async () => {
        try {
            const company_id = localStorage.getItem("companyID");
            const res = await axios.get(
                `${API_URL}/company-warehouses/${company_id}`
            );
            setWarehouses(res.data.data || []);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchWarehouses();
    }, []);


    useEffect(() => {
        dispatch(fetchBranchUsers());
        dispatch(fetchBranches());
        dispatch(fetchRoles());
    }, [dispatch]);

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    // Handle form submission with password confirmation check
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!branchUserData && formValues.password !== formValues.confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        const branchUserDataToSubmit = {
            name: formValues.name,
            email: formValues.email,
            roleId: formValues.roleId,
            parent_user: formValues.parent_user,
            mobile_no: formValues.mobile_no,
            branch_id: formValues.branch_id,
            account_holder_name: formValues.account_holder_name,
            account_number: formValues.account_number,
            bank_name: formValues.bank_name,
            bank_ifsc: formValues.bank_ifsc,
            bank_location: formValues.bank_location,
            tax_payer_id: formValues.tax_payer_id,
            warehouse_id: formValues.warehouse_id,
            ...(branchUserData ? {} : { password: formValues.password }),
        };

        try {
            if (branchUserData?.id) {
                await dispatch(
                    updateBranchUser({
                        id: branchUserData.id,
                        branchUserData: branchUserDataToSubmit,
                    })
                ).unwrap();

                Swal.fire({
                    toast: true,
                    icon: "success",
                    title: "Branch user updated successfully",
                    position: "top-end",
                    showConfirmButton: false,
                    timer: 2500,
                    timerProgressBar: true,
                    didOpen: (toast) => {
                        toast.addEventListener("mouseenter", Swal.stopTimer);
                        toast.addEventListener("mouseleave", Swal.resumeTimer);
                    },
                });
            } else {
                await dispatch(createBranchUser(branchUserDataToSubmit)).unwrap();

                Swal.fire({
                    toast: true,
                    icon: "success",
                    title: "Branch user created successfully",
                    position: "top-end",
                    showConfirmButton: false,
                    timer: 2500,
                    timerProgressBar: true,
                    didOpen: (toast) => {
                        toast.addEventListener("mouseenter", Swal.stopTimer);
                        toast.addEventListener("mouseleave", Swal.resumeTimer);
                    },
                });
            }

            // dispatch(fetchBranchUsers());

            // 🔁 Redirect after toast
            setTimeout(() => navigate("/branch/users"), 2600);

        } catch (error) {
            Swal.fire({
                toast: true,
                icon: "error",
                title: error?.message || "Something went wrong",
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
            });
        }
    };


    const options = branchUsers.filter(user => !branchUserData || branchUserData.id !== user.id).map(user => ({
        value: user.id,
        label: user.name
    }));

    return (
        <div className="row">
            <div className="col-xl-12 col-xxl-12">
                <div className="card">
                    <div className="card-header">
                        <h4 className="card-title">{branchUserData?.id ? "Edit User" : "Add User"} / Staff</h4>
                    </div>
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-md-4 mb-4 mt-2">
                                    <div className="position-relative">
                                        <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                            Name
                                        </span>
                                        <input
                                            type="text"
                                            name="name"
                                            className="form-control"
                                            value={formValues.name}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="col-md-4 mb-4 mt-2">
                                    <div className="position-relative">
                                        <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                            Email
                                        </span>
                                        <input
                                            type="email"
                                            name="email"
                                            className="form-control"
                                            value={formValues.email}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="col-md-4 mb-4 mt-2">
                                    <div className="position-relative">
                                        <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                            Mobile Number
                                        </span>
                                        <input
                                            type="text"
                                            name="mobile_no"
                                            className="form-control"
                                            value={formValues.mobile_no}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className={`${!branchUserData ? 'col-md-4' : 'col-md-3'} mb-4 mt-2`}>
                                    <div className="position-relative">
                                        <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                            Role / Designation
                                        </span>
                                        <select
                                            name="roleId"
                                            className="form-control"
                                            value={formValues.roleId}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="">Select Role</option>
                                            {roles.map((role) => (
                                                <option key={role.id} value={role.id}>
                                                    {role.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {!branchUserData && (<div className="col-md-4 mb-4 mt-2">
                                    <div className="position-relative">
                                        <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                            Password
                                        </span>
                                        <input
                                            type="password"
                                            name="password"
                                            className="form-control"
                                            value={formValues.password}
                                            onChange={handleInputChange}
                                            required={!branchUserData} // Required only when adding new user
                                        />
                                    </div>
                                </div>)}

                                {!branchUserData && (
                                    <div className="col-md-4 mb-4 mt-2">
                                        <div className="position-relative">
                                            <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                                Confirm Password
                                            </span>
                                            <input
                                                type="password"
                                                name="confirmPassword"
                                                className="form-control"
                                                value={formValues.confirmPassword}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>
                                    </div>
                                )}


                                <div className={`${!branchUserData ? 'col-md-4' : 'col-md-3'} mb-4 mt-2`}>
                                    <div className="position-relative">
                                        <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2" style={{ zIndex: 1, }}>
                                            Reporting To
                                        </span>
                                        {/* <select
                                            name="parent_user"
                                            className="form-control"
                                            value={formValues.parent_user}
                                            onChange={handleInputChange}
                                        
                                        >
                                            <option value="">Select User</option>
                                            {branchUsers.map((user) =>
                                                branchUserData && branchUserData.id != user.id ? (
                                                    <option key={user.id} value={user.id}>
                                                        {user.name}
                                                    </option>
                                                ) : <option key={user.id} value={user.id}>
                                                {user.name}
                                            </option>
                                            )}
                                        </select> */}

                                        <Select
                                            isMulti
                                            name="parent_user"
                                            options={options}
                                            defaultValue={branchUsers
                                                .filter(user => formValues.parent_user.includes(user.id.toString())) // Ensure type matches
                                                .map(user => ({ value: user.id, label: user.name }))}
                                            onChange={handleMultiSelectChange}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                        />


                                    </div>
                                </div>

                                <div className={`${!branchUserData ? 'col-md-4' : 'col-md-3'} mb-4 mt-2`}>
                                    <div className="position-relative">
                                        <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                            Branch / Department
                                        </span>
                                        <select
                                            name="branch_id"
                                            className="form-control"
                                            value={formValues.branch_id}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="">Select Branch</option>
                                            {branches.map((branch) => (
                                                <option key={branch.id} value={branch.id}>
                                                    {branch.branch_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className={`${!branchUserData ? 'col-md-4' : 'col-md-3'} mb-4 mt-2`}>
                                    <div className="position-relative">
                                        <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                            Warehouse
                                        </span>
                                        <select
                                            name="warehouse_id"
                                            className="form-control"
                                            value={formValues.warehouse_id}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="">Select Warehouse</option>
                                            {warehouses.map((warehouse) => (
                                                <option key={warehouse.id} value={warehouse.id}>
                                                    {warehouse.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-md-12 mb-3 mt-3">
                                    <h5>Bank Details</h5>
                                </div>

                                <div className="col-md-4 mb-4 mt-2">
                                    <div className="position-relative">
                                        <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                            Account Holder Name
                                        </span>
                                        <input
                                            type="text"
                                            name="account_holder_name"
                                            className="form-control"
                                            value={formValues.account_holder_name}
                                            onChange={handleInputChange}

                                        />
                                    </div>
                                </div>

                                <div className="col-md-4 mb-4 mt-2">
                                    <div className="position-relative">
                                        <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                            Account Number
                                        </span>
                                        <input
                                            type="text"
                                            name="account_number"
                                            className="form-control"
                                            value={formValues.account_number}
                                            onChange={handleInputChange}

                                        />
                                    </div>
                                </div>

                                <div className="col-md-4 mb-4 mt-2">
                                    <div className="position-relative">
                                        <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                            Bank Name
                                        </span>
                                        <input
                                            type="text"
                                            name="bank_name"
                                            className="form-control"
                                            value={formValues.bank_name}
                                            onChange={handleInputChange}

                                        />
                                    </div>
                                </div>


                                <div className="col-md-4 mb-4 mt-2">
                                    <div className="position-relative">
                                        <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                            IFSC Code
                                        </span>
                                        <input
                                            type="text"
                                            name="bank_ifsc"
                                            className="form-control"
                                            value={formValues.bank_ifsc}
                                            onChange={handleInputChange}

                                        />
                                    </div>
                                </div>

                                <div className="col-md-4 mb-4 mt-2">
                                    <div className="position-relative">
                                        <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                            Location
                                        </span>
                                        <input
                                            type="text"
                                            name="bank_location"
                                            className="form-control"
                                            value={formValues.bank_location}
                                            onChange={handleInputChange}

                                        />
                                    </div>
                                </div>


                                <div className="col-md-4 mb-4 mt-2">
                                    <div className="position-relative">
                                        <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                            Tax Payer Id
                                        </span>
                                        <input
                                            type="text"
                                            name="tax_payer_id"
                                            className="form-control"
                                            value={formValues.tax_payer_id}
                                            onChange={handleInputChange}

                                        />
                                    </div>
                                </div>

                                {error && (
                                    <div className="col-md-12">
                                        <p className="text-danger">{error}</p>
                                    </div>
                                )}

                                <div className="col-md-6">
                                    <button type="submit" className="btn btn-primary btn-block">
                                        {branchUserData?.id ? "Update" : "Submit"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddEditBranchUser;
